/**
 * @file dashboard-attention-groups.ts
 * @description Группы «Что нужно сделать» на «Обзоре склада» (phase19.31,
 * шаг О4; макет (в2) разбора зоны «Склад»).
 *
 * Сервер присылает события «Требует внимания» по одному (до 20 строк): было
 * «19 одинаковых плиток». Теперь одна строка на ВИД задачи (`kind`, а у
 * зависших черновиков — ещё и вид документа): название, «самая старая —
 * N дней назад», число и переход в отфильтрованный реестр.
 *
 * Правила:
 *  - число — точный счётчик сервера, если он есть для этого вида
 *    (`counters`), иначе — сколько строк пришло (черновики: сервер присылает
 *    не больше 10 на вид документа, см. отчёт фазы);
 *  - одна строка в группе — переход прямо в объект (прежний `attentionHref`)
 *    и подпись — фраза сервера; несколько — в реестр с отбором;
 *  - тон группы — «опасно», если среди событий есть `danger`; такие группы
 *    выше остальных, дальше — порядок реестра.
 * Новый вид задачи = запись в `ATTENTION_GROUPS`, а не ветка в разметке.
 */

import { attentionHref } from "./dashboard-attention";
import { wmsHref } from "./dashboard-href";
import type { WmsAttentionItemDto, WmsDashboardCountersDto } from "../../../lib/api-wms-dashboard";
import { pluralRu } from "../../../lib/plural";
import { getShopTimeZone } from "../../../lib/shop-time";
import type { AttentionItem } from "../../ui/AttentionList";

const DAY_MS = 24 * 60 * 60 * 1000;

/** Запись реестра групп: подпись, счётчик и реестр с отбором. */
interface AttentionGroupMeta {
  title: string;
  /** «самая старая» / «самое старое» — род слова в подписи возраста. */
  oldest?: string;
  /** Точный счётчик сервера для этого вида. */
  counter?: keyof WmsDashboardCountersDto;
  /** Подпись группы без времени события (несколько строк). */
  caption?: (items: WmsAttentionItemDto[]) => string | undefined;
  /**
   * `false` — фраза сервера только повторяет число («6 партий без этикеток»),
   * в подпись одиночной строки её не берём.
   */
  serverTitle?: boolean;
  /** Реестр с отбором — куда ведёт группа из нескольких строк. */
  registry: (base: string, warehouseId: string) => string;
}

/** «05.10» — день и месяц по часовому поясу магазина (SC31). */
function dayMonth(iso: string): string {
  return new Date(iso).toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", timeZone: getShopTimeZone() });
}

/**
 * Реестр групп; порядок = порядок строк внутри одного тона: сначала то, что
 * держит работу (сборка, непроведённые документы), потом порядок на складе.
 */
export const ATTENTION_GROUPS: Record<string, AttentionGroupMeta> = {
  shortage: {
    title: "Недостача при сборке",
    oldest: "самая старая",
    counter: "pickingShortage",
    registry: (base, wh) => wmsHref(base, "/picking", { tab: "shortage", wh }),
  },
  "stale_draft:receipt": {
    title: "Приёмки не проведены",
    oldest: "самая старая",
    registry: (base, wh) => wmsHref(base, "/receipts", { status: "DRAFT", wh }),
  },
  "stale_draft:transfer": {
    title: "Перемещения не проведены",
    oldest: "самое старое",
    // Отбора по статусу у реестра перемещений нет — открываем реестр.
    registry: (base) => `${base}/transfers`,
  },
  "stale_draft:writeoff": {
    title: "Списания не проведены",
    oldest: "самое старое",
    registry: (base) => `${base}/writeoffs`,
  },
  unplaced: {
    title: "Партии без места",
    oldest: "самая старая",
    counter: "unplacedLots",
    registry: (base, wh) => wmsHref(base, "/stock", { unplaced: "1", select: "all", wh }),
  },
  unlabeled: {
    title: "Партии без этикеток",
    // Подпись — только возраст, если сервер пришлёт дату; иначе подписи нет
    // (замечание владельца: «6 партий без этикеток» повторяло число справа).
    oldest: "самая старая",
    counter: "unlabeledLots",
    serverTitle: false,
    registry: (base, wh) => wmsHref(base, "/stock", { labels: "none", select: "all", wh }),
  },
  expiring: {
    title: "Истекает срок годности",
    counter: "expiringLots",
    caption: (items) => {
      if (items.some((item) => item.severity === "danger")) return "есть партии с истёкшим сроком";
      const nearest = items.map((item) => item.at).filter((at): at is string => !!at).sort()[0];
      return nearest ? `ближайший срок — ${dayMonth(nearest)}` : undefined;
    },
    registry: (base, wh) => wmsHref(base, "/expiring", { wh }),
  },
  below_min: {
    title: "Мало на складе",
    counter: "belowMinSkus",
    caption: () => "пора пополнить",
    registry: (base, wh) => wmsHref(base, "/stock", { belowMin: "1", positive: "0", wh }),
  },
};

const GROUP_ORDER = Object.keys(ATTENTION_GROUPS);

/** Ключ группы события: вид задачи, у черновиков — с видом документа. */
export function attentionGroupKey(item: WmsAttentionItemDto): string {
  return item.kind === "stale_draft" ? `stale_draft:${item.href.type}` : item.kind;
}

/** «самая старая — 16 дней назад» / «… — сегодня». */
export function ageCaption(oldest: string, iso: string, now: Date): string {
  const days = Math.max(0, Math.floor((now.getTime() - new Date(iso).getTime()) / DAY_MS));
  if (days === 0) return `${oldest} — сегодня`;
  return `${oldest} — ${days} ${pluralRu(days, ["день", "дня", "дней"])} назад`;
}

interface GroupParams {
  base: string;
  warehouseId: string;
  counters: WmsDashboardCountersDto | null;
  now: Date;
}

/** События сервера → строки «Что нужно сделать» (одна на вид задачи). */
export function groupAttention(items: WmsAttentionItemDto[], params: GroupParams): AttentionItem[] {
  const buckets = new Map<string, WmsAttentionItemDto[]>();
  for (const item of items) {
    const key = attentionGroupKey(item);
    buckets.set(key, [...(buckets.get(key) ?? []), item]);
  }

  const rank = (key: string) => (GROUP_ORDER.includes(key) ? GROUP_ORDER.indexOf(key) : GROUP_ORDER.length);
  const groups = [...buckets.entries()].map(([key, group]) => toRow(key, group as [WmsAttentionItemDto, ...WmsAttentionItemDto[]], params));
  return groups.sort((a, b) => toneRank(a) - toneRank(b) || rank(a.key) - rank(b.key));
}

function toneRank(row: AttentionItem): number {
  return row.tone === "danger" ? 0 : 1;
}

/** Группа всегда непустая: её создаёт первое же событие вида. */
function toRow(key: string, group: [WmsAttentionItemDto, ...WmsAttentionItemDto[]], params: GroupParams): AttentionItem {
  const meta = ATTENTION_GROUPS[key];
  const first = group[0];
  const tone = group.some((item) => item.severity === "danger") ? "danger" : "warning";
  const single = group.length === 1;
  const objectHref = attentionHref(params.base, first.href, first.kind, params.warehouseId || undefined);
  // Сервер новее клиента: вид без записи — строка как пришла, ссылка на объект.
  if (!meta) return { key, tone, title: first.title, count: group.length, to: objectHref };

  const exact = meta.counter ? params.counters?.[meta.counter] : undefined;
  const count = Math.max(exact ?? 0, group.length);
  const oldestAt = group.map((item) => item.at).filter((at): at is string => !!at).sort()[0];
  const age = meta.oldest && oldestAt ? ageCaption(meta.oldest, oldestAt, params.now) : undefined;
  // Одно событие — его фраза сервера (номер документа, партия), если она не
  // повторяет число; несколько — общая подпись вида или возраст самого старого.
  const caption = single && meta.serverTitle !== false ? first.title : (meta.caption?.(group) ?? age);

  return {
    key,
    tone,
    title: meta.title,
    caption,
    count,
    to: single ? objectHref : meta.registry(params.base, params.warehouseId),
  };
}
